/**
 * @fileoverview Turns any caught value into an AppError.
 * @module lib/errors/normalizeError
 */

import { AppError, ErrorCode } from "./AppError"

/**
 * Maps an HTTP status onto the closest error code.
 *
 * @param status - HTTP status code.
 * @returns The matching error code.
 */
export function codeFromStatus(status: number): ErrorCode {
	if (status === 401) return ErrorCode.UNAUTHORIZED
	if (status === 403) return ErrorCode.FORBIDDEN
	if (status === 404) return ErrorCode.NOT_FOUND
	if (status === 408 || status === 504) return ErrorCode.TIMEOUT
	return ErrorCode.HTTP
}

/**
 * Normalises anything throwable into an AppError.
 *
 * @description
 * `catch` binds `unknown`: a string, a DOMException, a Response, a plain
 * object. Everything funnels through here so the rest of the codebase only
 * ever deals with AppError.
 *
 * @param error - The caught value.
 * @param fallbackMessage - Message used when the value carries none.
 * @returns An AppError, with the original value kept as `cause`.
 *
 * @example
 * try { await save() } catch (error) { logError(normalizeError(error)) }
 */
export function normalizeError(
	error: unknown,
	fallbackMessage = "Une erreur inattendue est survenue"
): AppError {
	if (AppError.isAppError(error)) {
		return error
	}

	// A Response thrown by a router loader.
	if (error instanceof Response) {
		return new AppError(error.statusText || fallbackMessage, {
			code: codeFromStatus(error.status),
			status: error.status,
			cause: error,
		})
	}

	// An aborted fetch: AbortController or an expired deadline.
	if (error instanceof DOMException && error.name === "AbortError") {
		return new AppError("La requête a été interrompue", {
			code: ErrorCode.TIMEOUT,
			cause: error,
		})
	}

	// `fetch` rejects with a TypeError when the network is unreachable.
	if (error instanceof TypeError) {
		return new AppError(error.message || fallbackMessage, {
			code: ErrorCode.NETWORK,
			cause: error,
		})
	}

	if (error instanceof Error) {
		return new AppError(error.message || fallbackMessage, {
			code: ErrorCode.UNKNOWN,
			cause: error,
		})
	}

	if (typeof error === "string" && error.length > 0) {
		return new AppError(error, { code: ErrorCode.UNKNOWN })
	}

	return new AppError(fallbackMessage, {
		code: ErrorCode.UNKNOWN,
		cause: error,
		context: { raw: error },
	})
}
